// B10 S17 · the Action Center's filters (CR-092; plan D11; DC-45, DC-214): the query model and its controls. The
// route's query is the one source of truth: the page parses it, every control writes a new query back through the
// router, and a saved view (surface `action_center`, S10) stores the same filter as B1's `ListInput` keys. Kind chips in
// his nine words; the due and requisition filters and any tier filter as the bar's chips; *Priority · Due ·
// Requisition* as the sort. The Hiring Manager chip waits for C-B10-1 (`action_center.list` takes no hiring manager).

import type { ReactNode } from "react";
import { Chip, Chips, Count, FilterBar, Flex, Select, type ActionKind, type FilterBarFilter, type SelectOption } from "@/mc";
import {
  ACTION_KINDS, ACTION_SORTS, ACTION_TIERS, DUE_WINDOWS, HISTORY_PAGE, OPEN_PAGE,
  type ActionPagesInput, type ActionSort, type ActionTab, type ActionTier, type DueWindow,
} from "./api";
import { AC_LABELS, DUE_WORD, KIND_WORD, PAGE_WORDS, SORT_WORD, TIER_HEADING } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

export interface ActionQuery {
  tab: ActionTab;
  /** In his order, once each. */
  kinds: readonly ActionKind[];
  /** In tier order, once each. */
  tiers: readonly ActionTier[];
  due: DueWindow | null;
  /** `R-<digits>`. */
  requisition: string | null;
  sort: ActionSort;
}

export const DEFAULT_QUERY: ActionQuery = { tab: "open", kinds: [], tiers: [], due: null, requisition: null, sort: "priority" };

const REQUISITION_ID = /^R-\d+$/;

/** A key's values, repeated (`kind=a&kind=b`) or comma-joined (`kind=a,b`). */
function values(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).flatMap((value) => value.split(",")).map((value) => value.trim()).filter(Boolean);
}

function dueOf(value: string | null | undefined): DueWindow | null {
  return DUE_WINDOWS.find((due) => due === value && DUE_WORD[due] !== undefined) ?? null;
}

function requisitionOf(value: unknown): string | null {
  return typeof value === "string" && REQUISITION_ID.test(value) ? value : null;
}

/**
 * The query of `#action-center?…` (a URLSearchParams, a query string, or a whole hash). Unknown values are dropped,
 * so a link or a saved view can never apply a filter the screen does not show.
 */
export function parseActionQuery(source: URLSearchParams | string): ActionQuery {
  const params = typeof source === "string" ? new URLSearchParams(source.includes("?") ? source.slice(source.indexOf("?") + 1) : source) : source;
  const kinds = values(params, "kind");
  const tiers = values(params, "tier");
  return {
    tab: params.get("tab") === "history" ? "history" : "open",
    kinds: ACTION_KINDS.filter((kind) => kinds.includes(kind)),
    tiers: ACTION_TIERS.filter((tier) => tiers.includes(tier)),
    due: dueOf(params.get("due")),
    requisition: requisitionOf(params.get("req")),
    sort: ACTION_SORTS.find((sort) => sort === params.get("sort")) ?? "priority",
  };
}

/** The query string, in a fixed order with the defaults left out (`tab=history`, `kind`, `tier`, `due`, `req`, `sort`). */
export function actionSearch(query: ActionQuery): string {
  const params = new URLSearchParams();
  if (query.tab === "history") params.set("tab", "history");
  for (const kind of ACTION_KINDS) if (query.kinds.includes(kind)) params.append("kind", kind);
  for (const tier of ACTION_TIERS) if (query.tiers.includes(tier)) params.append("tier", tier);
  if (query.due && DUE_WORD[query.due] !== undefined) params.set("due", query.due);
  if (query.requisition && REQUISITION_ID.test(query.requisition)) params.set("req", query.requisition);
  if (query.sort !== "priority") params.set("sort", query.sort);
  return params.toString();
}

export function actionHref(query: ActionQuery): string {
  const search = actionSearch(query);
  return search ? `#action-center?${search}` : "#action-center";
}

/** DC-214 "Approvals and the Action Center hold the same rows, once.": *Approvals* is this page on the approvals kind. */
export const APPROVALS_HREF = actionHref({ ...DEFAULT_QUERY, kinds: ["approvals"] });

export function isApprovals(query: ActionQuery): boolean {
  return query.kinds.length === 1 && query.kinds[0] === "approvals";
}

/** B1's input for this query: only the keys set, a page of B1's largest size on the open tab. */
export function pageInput(query: ActionQuery): ActionPagesInput {
  const input: ActionPagesInput = { tab: query.tab, sort: query.sort, limit: query.tab === "open" ? OPEN_PAGE : HISTORY_PAGE };
  if (query.kinds.length) input.kinds = [...query.kinds];
  if (query.tiers.length) input.tiers = [...query.tiers];
  if (query.due) input.due = query.due;
  if (query.requisition) input.requisition_id = query.requisition;
  return input;
}

/**
 * A saved view's spec (surface `action_center`): the filter as B1's `ListInput` keys, which S10 validates at save and
 * at apply; never the tab, the page size or a cursor (a view is a filter, not a place in a list).
 */
export function toSpec(query: ActionQuery): Record<string, unknown> {
  const spec: Record<string, unknown> = { sort: query.sort };
  if (query.kinds.length) spec["kinds"] = [...query.kinds];
  if (query.tiers.length) spec["tiers"] = [...query.tiers];
  if (query.due) spec["due"] = query.due;
  if (query.requisition) spec["requisition_id"] = query.requisition;
  return spec;
}

/** The query a saved view's spec gives, on the tab the reader is on; a value the screen does not know is dropped. */
export function fromSpec(spec: Readonly<Record<string, unknown>>, tab: ActionTab): ActionQuery {
  const list = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []);
  const kinds = list(spec["kinds"]);
  const tiers = list(spec["tiers"]);
  const due = spec["due"];
  return {
    tab,
    kinds: ACTION_KINDS.filter((kind) => kinds.includes(kind)),
    tiers: ACTION_TIERS.filter((tier) => tiers.includes(tier)),
    due: dueOf(typeof due === "string" ? due : null),
    requisition: requisitionOf(spec["requisition_id"]),
    sort: ACTION_SORTS.find((sort) => sort === spec["sort"]) ?? "priority",
  };
}

export function withKind(query: ActionQuery, kind: ActionKind, on: boolean): ActionQuery {
  const kinds = on ? [...query.kinds, kind] : query.kinds.filter((each) => each !== kind);
  return { ...query, kinds: ACTION_KINDS.filter((each) => kinds.includes(each)) };
}

export interface KindChipsProps {
  query: ActionQuery;
  /** The open rows per kind (`kind:<category>`); absent on History, whose rows are not counted per kind. */
  counts?: Readonly<Record<string, number>> | undefined;
  onChange: (next: ActionQuery) => void;
}

/** DC-45 "kind chips in his nine words": one toggle chip per kind, in his order, with the open count beside the word. */
export function KindChips({ query, counts, onChange }: KindChipsProps) {
  return (
    <Chips>
      {ACTION_KINDS.map((kind) => (
        <Chip
          key={kind}
          sm
          on={query.kinds.includes(kind)}
          onToggle={(on) => onChange(withKind(query, kind, on))}
          hs={hsKey("ac.chip", kind)}
        >
          {KIND_WORD[kind]}
          {counts ? <Count n={counts[`kind:${kind}`] ?? 0} /> : null}
        </Chip>
      ))}
    </Chips>
  );
}

export interface SeenRequisition {
  id: string;
  /** Null where the reader may not open it (B1 sends no title then). */
  title: string | null;
}

export interface ActionFilterBarProps {
  query: ActionQuery;
  /** The requisitions of the rows read so far, for the requisition filter. */
  requisitions: readonly SeenRequisition[];
  onChange: (next: ActionQuery) => void;
  /** S19's *Saved views* select, mounted beside the bar. */
  savedViews?: ReactNode;
}

function requisitionWords(item: SeenRequisition): string {
  return item.title ? `${item.title} · ${item.id}` : item.id;
}

/**
 * The filter bar: the due date as the bar's date select, the active tier, due and requisition filters as chips with
 * their remove, and the sort on *Prioritized actions* (History is newest first). The requisition select and *Saved
 * views* sit beside it.
 */
export function ActionFilterBar({ query, requisitions, onChange, savedViews }: ActionFilterBarProps) {
  const filters: FilterBarFilter[] = [];
  for (const tier of query.tiers) {
    filters.push({
      name: AC_LABELS.priority,
      value: TIER_HEADING[tier],
      onRemove: () => onChange({ ...query, tiers: query.tiers.filter((each) => each !== tier) }),
      hs: hsKey("ac.filter.tier", tier),
    });
  }
  const dueWord = query.due ? DUE_WORD[query.due] : undefined;
  if (query.due && dueWord) {
    filters.push({ name: AC_LABELS.dueDate, value: dueWord, onRemove: () => onChange({ ...query, due: null }), hs: "ac.filter.due" });
  }
  const chosen = requisitions.find((item) => item.id === query.requisition);
  if (query.requisition) {
    filters.push({
      name: AC_LABELS.requisition,
      value: chosen ? requisitionWords(chosen) : query.requisition,
      onRemove: () => onChange({ ...query, requisition: null }),
      hs: "ac.filter.req",
    });
  }
  const dueOptions: SelectOption[] = DUE_WINDOWS.flatMap((due) => {
    const word = DUE_WORD[due];
    return word ? [{ value: due, label: word }] : [];
  });
  const sortOptions: SelectOption[] = ACTION_SORTS.map((sort) => ({ value: sort, label: SORT_WORD[sort] }));
  const requisitionOptions: SelectOption[] = requisitions.map((item) => ({ value: item.id, label: requisitionWords(item) }));
  return (
    <Flex wrap gap={10} align="center">
      <FilterBar
        label={query.tab === "open" ? PAGE_WORDS.prioritizedActions : PAGE_WORDS.history}
        date={{ options: dueOptions, value: query.due ?? "", onChange: (value) => onChange({ ...query, due: dueOf(value) }), hs: "ac.due" }}
        filters={filters}
        sort={query.tab === "open"
          ? { options: sortOptions, value: query.sort, onChange: (value) => onChange({ ...query, sort: ACTION_SORTS.find((sort) => sort === value) ?? "priority" }), hs: "ac.sort" }
          : undefined}
      />
      <Select
        sm
        aria-label={AC_LABELS.requisition}
        options={requisitionOptions}
        value={query.requisition ?? ""}
        onChange={(value) => onChange({ ...query, requisition: requisitionOf(value) })}
        
      />
      {savedViews ?? null}
    </Flex>
  );
}
